import React, { useContext, useState, useEffect } from 'react';
import { withApollo } from 'react-apollo';
import AppContext from '../../../../context';
import { ContextName } from '../../../../appContext/contextName';
import DiscoveryContext from '../../../../contexts/discoveryContext/contextV2';
import { SearchEventsV2 } from '../../../../state/services/gql/content';
import { whitelistInput } from '../../../../utils/getSearchQueryParams';
import { stringifyQS } from '../../../../utils';
import SportSlices from './component';

const SlicesConfig = [
  {
    showOnSportId: -1, // All Events
    slices: [
      {
        title: 'Running Events',
        subtitle: 'Laces Tied. Ready to Go',
        searchParams: { sport: 3 },
        path: (countryCode) => `/${countryCode}/running-events`,
      },
      {
        title: 'Triathlons',
        subtitle: 'Swim. Bike. Run. Repeat.',
        searchParams: { sport: 6 },
        path: (countryCode) => `/${countryCode}/triathlon-events`,
      },
      {
        title: 'Cycling Events',
        subtitle: 'In the Saddle',
        searchParams: { sport: 39 },
        path: (countryCode) => `/${countryCode}/road-cycling-events`,
      },
    ],
  },
  {
    showOnSportId: 3,
    slices: [
      {
        title: '10kms',
        searchParams: { sport: 3, sportDistances: [168] },
        path: (countryCode) => `/${countryCode}/running-events`,
        hide: (context) => context?.activeFilters?.distance,
      },
      {
        title: 'Half Marathons',
        searchParams: { sport: 3, sportDistances: [170] },
        path: (countryCode) => `/${countryCode}/running-events`,
        hide: (context) => context?.activeFilters?.distance,
      },
      {
        title: 'Trail Runs',
        searchParams: { sport: 3, tags: ['trail-runs'] },
        path: (countryCode) => `/${countryCode}/running-events/trail`,
        hide: (context) => context?.activeFilters?.tag,
      },
      {
        title: 'Road Runs',
        searchParams: { sport: 3, tags: ['road-runs'] },
        path: (countryCode) => `/${countryCode}/running-events/road`,
        hide: (context) => context?.activeFilters?.tag,
      },
    ],
  },
  {
    showOnSportId: 6,
    slices: [
      {
        title: 'Sprint Triathlons',
        subtitle: 'Short and Sweet',
        searchParams: { sport: 6, sportDistances: [172] },
        path: (countryCode) => `/${countryCode}/triathlon-events`,
        hide: (context) => context?.activeFilters?.distance,
      },
      {
        title: 'Olympic Triathlons',
        subtitle: 'Go for Gold',
        searchParams: { sport: 6, sportDistances: [130] },
        path: (countryCode) => `/${countryCode}/triathlon-events`,
        hide: (context) => context?.activeFilters?.distance,
      },
    ],
  },
];

const SportSliceContainer = ({ client }) => {
  const [slices, setSlices] = useState();
  const {
    [ContextName.COUNTRY_CODE]: countryCode,
    [ContextName.USER_SELECTED_LOCALE]: userSelectedLocale,
  } = useContext(AppContext);
  const context = useContext(DiscoveryContext);
  const {
    filters: {
      sport: { id: sportId },
    },
    activeFilters: {
      distance: isDistanceFilterActive,
      tag: isTagFilterActive,
      covid: isCovidFilterActive,
    },
    queryParams,
  } = context;

  const slice = SlicesConfig.find((s) => s.showOnSportId === sportId);

  const shownSlices =
    slice?.slices.filter((s) => {
      if (s.hide) {
        return !s.hide(context);
      }
      return true;
    }) ?? [];

  useEffect(() => {
    if (!slice) {
      return;
    }

    (async () => {
      const slicesWithData = await Promise.all(
        shownSlices.map(async (s) => {
          const { data } = await client.query({
            query: SearchEventsV2,
            variables: {
              searchParametersInput: whitelistInput({
                ...queryParams(),
                ...s.searchParams,
                resultsPerPage: 5,
              }),
            },
          });
          return { ...s, data };
        }),
      );

      const loweredCountryCode = (
        userSelectedLocale || countryCode
      ).toLowerCase();
      const slicesWithLinks = slicesWithData.map((s) => {
        const url = `${s.path(loweredCountryCode)}?${stringifyQS({
          ...queryParams(),
          ...s.searchParams,
        })}`;

        return { ...s, seeAllUrl: url };
      });

      setSlices(slicesWithLinks);
    })();
  }, [sportId, context.filters]);

  if (isDistanceFilterActive || isTagFilterActive || isCovidFilterActive) {
    return null;
  }

  if (!slice || shownSlices.length === 0) {
    return null;
  }

  return <SportSlices slices={slices} />;
};

export default withApollo(SportSliceContainer);
